/**
 * The skin AS THE FILE PAINTED IT — recorded at the load door, before any of
 * the run's weight doors touch it (2026-09-23, the tail that climbed the back).
 *
 * WHY. The Document run re-paints the subject in place: ADR-029 hands the
 * buttock and lower back to the pelvis, the hinge bands move crease flesh to
 * helper bones, T2 binds the tail to its chain — and grafts append bones. The
 * zones panel measures that re-painted subject. The tail detector's owner cue
 * ("the pelvis owns flesh behind the hips") therefore read the lower back the
 * re-own had just handed to the pelvis as tail, and on the kobold its zone
 * climbed 15 cm up the back (117 cm, root above the hips) where the same
 * detector on the file read 94 cm with the root at the tail's base. A zone
 * proposal reads the body as authored; the run's corrections are the run's.
 *
 * WHAT. One copy per skinned geometry of the file's skinIndex/skinWeight and
 * the skeleton's bones as loaded (by OBJECT — grafts append, and a name can
 * be worn by a non-bone). Kept in a WeakMap, never in userData: the export
 * door writes userData into extras.
 *
 * Depends on: three. Depended on by: the load doors (loadGLB, loadFBX) and
 * subjectArrays' `paint: 'file'`.
 */
import type { Bone, BufferGeometry, Object3D, SkinnedMesh } from 'three';

export interface FilePaint {
  readonly index: Uint16Array;
  readonly weight: Float32Array;
  /** The skeleton's bones at load, in skin order — the bones `index` addresses. */
  readonly bones: readonly Bone[];
}

const painted = new WeakMap<BufferGeometry, FilePaint>();

/** Record every skinned mesh's paint as loaded. Idempotent per geometry: the FIRST record is the file's. */
export function recordFilePaint(scene: Object3D): void {
  scene.traverse((node) => {
    const mesh = node as SkinnedMesh;
    if (mesh.isSkinnedMesh !== true || painted.has(mesh.geometry)) return;
    const skinIndex = mesh.geometry.getAttribute('skinIndex');
    const skinWeight = mesh.geometry.getAttribute('skinWeight');
    if (!skinIndex || !skinWeight) return;
    const n = skinIndex.count;
    const index = new Uint16Array(n * 4);
    const weight = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) {
      for (let k = 0; k < 4; k++) {
        index[i * 4 + k] = skinIndex.getComponent(i, k);
        weight[i * 4 + k] = skinWeight.getComponent(i, k);
      }
    }
    painted.set(mesh.geometry, { index, weight, bones: mesh.skeleton.bones.slice() });
  });
}

/** The file's paint for this mesh, or null when it was not loaded through a recording door. */
export function filePaintOf(mesh: SkinnedMesh): FilePaint | null {
  return painted.get(mesh.geometry) ?? null;
}
